import React, { useEffect } from 'react';
import { useOrganizations } from '../hooks/useOrganizations';
import { usePageMetadata } from '../contexts/PageMetadataContext';
import OrgReportsTabContent from '../components/organization-reports/OrgReportsTabContent';
import { AlertCircle } from 'lucide-react';
import LoadingList from '@shared/ui/LoadingList';

/**
 * Org Reports Page
 *
 * Organization-level reports hub. Aggregates standard workspace reports
 * (Hot Bills, Bill Tracking, Legislators, Votes, Scorecard) across all
 * workspaces and includes the SOS Report as a 6th tab.
 */
const OrgReportsPage: React.FC = () => {
  const { setPageMetadata } = usePageMetadata();
  const { proOrganizations, loading: organizationsLoading } = useOrganizations();

  useEffect(() => {
    setPageMetadata({
      title: 'Organization Reports',
      description: 'Organization-level reports across all workspaces',
    });
    return () => setPageMetadata(null);
  }, [setPageMetadata]);

  // Pro orgs only — the citizen membership is not a working context.
  const primaryOrg =
    proOrganizations.find(
      (org) =>
        org.role?.toLowerCase() === 'org_admin' ||
        org.role?.toLowerCase() === 'system_admin'
    ) || proOrganizations[0];

  if (organizationsLoading) {
    return (
      <LoadingList rows={4} label="Loading reports" />
    );
  }

  if (!primaryOrg) {
    return (
      <div className="card p-8 text-center">
        <AlertCircle className="h-12 w-12 text-warning-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-2">
          No Organization Found
        </h2>
        <p className="text-text-secondary dark:text-text-dark-secondary">
          You need to be a member of an organization to access Reports.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <OrgReportsTabContent
        organizationId={primaryOrg.id}
        organizationName={primaryOrg.name}
        organizationLogoUrl={primaryOrg.logo_url}
      />
    </div>
  );
};

export default OrgReportsPage;
